import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Heart,
    ShareNetwork,
    Camera,
    CaretUp,
    CaretDown,
    CaretRight,
    CaretLeft,
    WhatsappLogo,
    TelegramLogo,
    Copy,
    Spinner,
    SpeakerHigh,
    SpeakerSlash,
    X,
} from "@phosphor-icons/react";
import { fetchPropertiesForReels, getAccessToken } from "@/lib/api";
import { useFavorites } from "@/store/favoritesStore";
import { HeartColorPicker } from "@/components/HeartColorPicker";

const FIRST_LIMIT = 15;
const NEXT_LIMIT = 5;
const PREFETCH_LEFT = 10;
const NEAR_SLIDES = 2;
const NEAR_IMAGES = 2;
const AUTO_MS = 3500;

const VOICE_KEY = "voiceStatus";

const readVoiceActive = () => {
    try {
        const v = localStorage.getItem(VOICE_KEY);
        if (v === null) {
            localStorage.setItem(VOICE_KEY, "1");
            return true;
        }
        return v === "1";
    } catch {
        return true;
    }
};

const trackContext = require.context("../assets/mp3", false, /\.mp3$/);

const TRACKS = trackContext
    .keys()
    .sort()
    .map((key) => {
        const mod = trackContext(key);
        return mod.default ?? mod;
    });

const trackFor = (id) => {
    if (!TRACKS.length) return null;
    const n = Math.abs(Number(id)) || 0;
    return TRACKS[n % TRACKS.length];
};

const TAB_TO_BACKEND_TAB = {
    web: "web",
    favorites: "favorites",
    popular: "hot",
    notes: "notes",
};

const isMobileDevice = () => typeof window !== "undefined" && window.innerWidth < 768;

const getPageItems = (page) => {
    if (!page || page.success === false) return [];
    if (Array.isArray(page.data)) return page.data;
    if (Array.isArray(page.data?.data)) return page.data.data;
    return [];
};

const isValidImageUrl = (s) => typeof s === "string" && /^https?:\/\/\S+$/i.test(s.trim());

const getImages = (p) => {
    const raw = Array.isArray(p.images) && p.images.length ? p.images : p.image ? [p.image] : [];
    return raw.filter(isValidImageUrl).map((s) => s.trim());
};

const detailPath = (p) => `/detail/${p.propertyCode}`;

const roomNumber = (room) => {
    const m = String(room ?? "").match(/\d+/);
    return m ? m[0] : null;
};

const fmtPriceSpaced = (v) =>
    v == null || v === "" ? "" : Number(v).toLocaleString("ru-RU");

const fmtArea = (v, sep) => String(v).replace(".", sep);

const getTitle = (p) => {
    const rooms = roomNumber(p.room);
    const kind = (p.buildingType ?? p.propertyType ?? "").toLowerCase();
    const first = rooms ? `${rooms}-otaqlı ${kind}`.trim() : p.propertyType;

    return [
        first,
        p.metro,
        p.generalArea ? `${fmtArea(p.generalArea, ".")} m²` : null,
    ]
        .filter(Boolean)
        .join(", ");
};

const getSubtitle = (p) => {
    const rooms = roomNumber(p.room);
    return [
        rooms ? `${rooms} otaq` : null,
        p.generalArea ? `${fmtArea(p.generalArea, ",")} m²` : null,
        p.region,
        p.metro,
    ]
        .filter(Boolean)
        .join(" · ");
};

const pad = (n) => String(n).padStart(2, "0");

const fmtReelDate = (value) => {
    if (!value) return "";
    const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return "";

    const date = new Date(y, m - 1, d);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((today - date) / 86400000);

    if (diff === 0) return "Bu gün";
    if (diff === 1) return "Dünən";
    return `${pad(d)}.${pad(m)}.${y}`;
};

const copyToClipboard = (text) => {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
    return new Promise((resolve, reject) => {
        try {
            const el = document.createElement("textarea");
            el.value = text;
            el.style.position = "fixed";
            el.style.opacity = "0";
            document.body.appendChild(el);
            el.focus();
            el.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(el);
            ok ? resolve() : reject(new Error("copy failed"));
        } catch (e) {
            reject(e);
        }
    });
};

function ReelSlide({ p, near, active, onShare, onNeedLogin, onBroken }) {
    const trackRef = useRef(null);
    const heartRef = useRef(null);
    const fillRef = useRef(null);
    const holdRef = useRef(false);
    const settleRef = useRef(null);
    const [img, setImg] = useState(0);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [localColor, setLocalColor] = useState(undefined);
    const [badSrc, setBadSrc] = useState(() => new Set());

    const allImages = useMemo(() => getImages(p), [p]);
    const images = useMemo(() => allImages.filter((s) => !badSrc.has(s)), [allImages, badSrc]);

    const n = images.length;
    const loop = n > 1;

    const slides = useMemo(
        () => (loop ? [images[n - 1], ...images, images[0]] : images),
        [images, loop, n]
    );

    const realOf = (raw) => (loop ? (((raw - 1) % n) + n) % n : raw);
    const dist = (a, b) => {
        const d = Math.abs(a - b);
        return loop ? Math.min(d, n - d) : d;
    };

    const getColor = useFavorites((s) => s.getColor);
    const setColor = useFavorites((s) => s.setColor);

    const hasToken = Boolean(getAccessToken());
    const storeColor = hasToken ? getColor(p) : null;
    const serverColor = hasToken ? p.color || null : null;
    const favColor = localColor !== undefined ? localColor : storeColor ?? serverColor;

    useEffect(() => {
        if (allImages.length > 0 && images.length === 0) {
            onBroken(p.idProperty);
        }
    }, [allImages.length, images.length, onBroken, p.idProperty]);

    useLayoutEffect(() => {
        const el = trackRef.current;
        if (!el || !loop) return;
        el.scrollLeft = el.clientWidth;
        setImg(0);
    }, [loop, n]);

    useEffect(() => () => clearTimeout(settleRef.current), []);

    const onImgError = (src) => {
        setBadSrc((prev) => {
            if (prev.has(src)) return prev;
            const next = new Set(prev);
            next.add(src);
            return next;
        });
    };

    const settle = () => {
        const el = trackRef.current;
        if (!el || !loop || !el.clientWidth) return;
        const w = el.clientWidth;
        const raw = Math.round(el.scrollLeft / w);
        if (raw === 0) el.scrollLeft = n * w;
        else if (raw === n + 1) el.scrollLeft = w;
    };

    const onTrackScroll = (e) => {
        const el = e.currentTarget;
        if (!el.clientWidth) return;
        const raw = Math.round(el.scrollLeft / el.clientWidth);
        setImg(realOf(raw));
        if (loop) {
            clearTimeout(settleRef.current);
            settleRef.current = setTimeout(settle, 120);
        }
    };

    const goImg = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        settle();
        el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };

    useEffect(() => {
        if (!active || !near || !loop || pickerOpen) return;

        if (fillRef.current) fillRef.current.style.width = "0%";

        let raf;
        let last = null;
        let elapsed = 0;

        const tick = (now) => {
            if (last === null) last = now;
            if (!holdRef.current && !document.hidden) elapsed += now - last;
            last = now;

            const pr = Math.min(elapsed / AUTO_MS, 1);
            if (fillRef.current) fillRef.current.style.width = `${pr * 100}%`;

            if (pr >= 1) {
                goImg(1);
                return;
            }
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [active, near, loop, pickerOpen, img]);

    const hold = () => {
        holdRef.current = true;
    };

    const release = () => {
        holdRef.current = false;
    };

    const onHeart = (e) => {
        e.stopPropagation();
        if (!getAccessToken()) return onNeedLogin();
        setPickerOpen((v) => !v);
    };

    const onSelectColor = (color) => {
        setColor(p, color);
        setLocalColor(color || null);
        toast(color ? "Seçilmişlərə əlavə olundu" : "Seçilmişlərdən silindi");
    };

    const openDetail = () => {
        window.open(detailPath(p), "_blank", "noopener,noreferrer");
    };

    const total = n || 1;
    const opRent =
        p.operationType?.toLowerCase().includes("kir") ||
        p.operationType?.toLowerCase().includes("günl");

    return (
        <section data-reel-slide className="relative h-full w-full snap-start snap-always bg-neutral-900">
            <div
                ref={trackRef}
                onScroll={onTrackScroll}
                onPointerDown={hold}
                onPointerUp={release}
                onPointerCancel={release}
                onPointerLeave={release}
                className="absolute inset-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hidden"
                style={{ scrollbarWidth: "none" }}
            >
                {n ? (
                    slides.map((src, i) => (
                        <div key={i} className="h-full w-full shrink-0 snap-center bg-neutral-900">
                            {near && dist(realOf(i), img) <= NEAR_IMAGES && (
                                <img
                                    src={src}
                                    alt={getTitle(p)}
                                    decoding="async"
                                    draggable={false}
                                    onError={() => onImgError(src)}
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </div>
                    ))
                ) : (
                    <div className="h-full w-full shrink-0 flex items-center justify-center text-white/40">
                        Şəkil yoxdur
                    </div>
                )}
            </div>

            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

            <div className="absolute top-2 inset-x-3 flex gap-1 z-10 pointer-events-none">
                {Array.from({ length: total }).map((_, i) => (
                    <div key={i} className="h-[3px] flex-1 rounded bg-white/30 overflow-hidden">
                        {i === img ? (
                            <div
                                ref={fillRef}
                                className="h-full bg-white"
                                style={{ width: loop ? "0%" : "100%" }}
                            />
                        ) : (
                            <div
                                className="h-full bg-white"
                                style={{ width: i < img ? "100%" : "0%" }}
                            />
                        )}
                    </div>
                ))}
            </div>

            {loop && (
                <>
                    <button
                        type="button"
                        onClick={() => goImg(-1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center z-10"
                    >
                        <CaretLeft size={18} weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={() => goImg(1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center z-10"
                    >
                        <CaretRight size={18} weight="bold" />
                    </button>
                </>
            )}

            <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5 text-white z-10">
                <div className="relative">
                    <button
                        ref={heartRef}
                        type="button"
                        onClick={onHeart}
                        className="flex flex-col items-center text-[11px] font-semibold"
                    >
                        <Heart
                            size={34}
                            weight={favColor ? "fill" : "regular"}
                            style={favColor ? { color: favColor } : undefined}
                        />
                        Bəyən
                    </button>
                    {pickerOpen && (
                        <HeartColorPicker
                            activeColor={favColor}
                            onSelect={onSelectColor}
                            onClose={() => setPickerOpen(false)}
                            anchorRef={heartRef}
                        />
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => onShare(p)}
                    className="flex flex-col items-center text-[11px] font-semibold"
                >
                    <ShareNetwork size={30} />
                    Paylaş
                </button>

                <div className="flex flex-col items-center text-[11px] font-semibold">
                    <Camera size={26} />
                    {Math.min(img + 1, total)}/{total}
                </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 p-4 pb-6 text-white z-10 pr-20 pointer-events-none">
                <div className="flex items-center gap-2 text-xs">
                    {p.operationType && (
                        <span className={`px-2.5 py-1 rounded-full font-bold ${opRent ? "bg-amber-500" : "bg-blue-600"}`}>
                            {p.operationType}
                        </span>
                    )}
                    {p.propertyDate && <span className="text-white/80">{fmtReelDate(p.propertyDate)}</span>}
                </div>

                <div className="mt-2 text-3xl font-extrabold">{fmtPriceSpaced(p.price)} ₼</div>
                <div className="mt-1 font-semibold leading-snug">{getTitle(p)}</div>
                <div className="mt-1 text-sm text-white/80">{getSubtitle(p)}</div>

                <button
                    type="button"
                    onClick={openDetail}
                    className="pointer-events-auto mt-4 flex h-12 w-full items-center justify-center rounded-full bg-teal-700 font-bold text-white"
                >
                    Elana bax
                </button>
            </div>
        </section>
    );
}

export function ReelsViewer({ tab, sortType, token, onClose }) {
    const nav = useNavigate();
    const scrollRef = useRef(null);
    const audioRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const mountedRef = useRef(false);
    const poppedRef = useRef(false);
    const shiftRef = useRef(0);
    const itemsRef = useRef([]);
    const activeRef = useRef(0);

    const [activeIndex, setActiveIndex] = useState(0);
    const [shareItem, setShareItem] = useState(null);
    const [loginModal, setLoginModal] = useState(false);
    const [muted, setMuted] = useState(() => !readVoiceActive());
    const [failed, setFailed] = useState(() => new Set());

    onCloseRef.current = onClose;

    const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ["reels", tab, sortType, Boolean(token)],
        queryFn: ({ pageParam = null }) =>
            fetchPropertiesForReels({
                limit: pageParam === null ? FIRST_LIMIT : NEXT_LIMIT,
                cursor: pageParam,
                tab: TAB_TO_BACKEND_TAB[tab] ?? tab,
                sortType,
            }),
        initialPageParam: null,
        getNextPageParam: (last) => {
            if (!last || last.success === false) return undefined;
            const list = getPageItems(last);
            if (list.length === 0) return undefined;
            return last.data?.nextCursor ?? last.nextCursor ?? undefined;
        },
        gcTime: 0,
        refetchOnWindowFocus: false,
    });

    const noPackage = useMemo(
        () => (data?.pages ?? []).some((pg) => pg && pg.success === false),
        [data]
    );

    const items = useMemo(() => {
        const map = new Map();
        (data?.pages ?? []).flatMap(getPageItems).forEach((p) => {
            if (map.has(p.idProperty)) return;
            if (failed.has(p.idProperty)) return;
            if (getImages(p).length === 0) return;
            map.set(p.idProperty, p);
        });
        return [...map.values()];
    }, [data, failed]);

    itemsRef.current = items;
    activeRef.current = activeIndex;

    const activeId = items[activeIndex]?.idProperty;
    const nextId = items[activeIndex + 1]?.idProperty;

    const onBroken = useCallback((id) => {
        const idx = itemsRef.current.findIndex((x) => x.idProperty === id);
        if (idx !== -1 && idx < activeRef.current) {
            shiftRef.current += 1;
        }
        setFailed((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    useLayoutEffect(() => {
        if (!shiftRef.current) return;
        const shift = shiftRef.current;
        shiftRef.current = 0;
        const el = scrollRef.current;
        if (el) el.scrollTop = Math.max(0, el.scrollTop - shift * el.clientHeight);
        setActiveIndex((cur) => Math.max(0, cur - shift));
    }, [failed]);

    useEffect(() => {
        mountedRef.current = true;

        if (!window.history.state?.reelsOpen) {
            window.history.pushState({ ...(window.history.state || {}), reelsOpen: true }, "");
        }

        const onPop = () => {
            poppedRef.current = true;
            onCloseRef.current?.();
        };

        window.addEventListener("popstate", onPop);

        return () => {
            mountedRef.current = false;
            window.removeEventListener("popstate", onPop);
            if (poppedRef.current) return;
            setTimeout(() => {
                if (!mountedRef.current && window.history.state?.reelsOpen) {
                    window.history.back();
                }
            }, 0);
        };
    }, []);

    useEffect(() => {
        if (!items.length || !hasNextPage || isFetchingNextPage) return;
        const remaining = items.length - 1 - activeIndex;
        if (remaining <= PREFETCH_LEFT) {
            fetchNextPage();
        }
    }, [activeIndex, items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const a = new Audio();
        a.loop = true;
        a.preload = "none";
        audioRef.current = a;
        return () => {
            a.pause();
            a.removeAttribute("src");
            a.load();
            audioRef.current = null;
        };
    }, []);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        if (muted || activeId == null) {
            a.pause();
            return;
        }

        const src = trackFor(activeId);
        if (!src) return;

        if (!a.src.endsWith(src)) {
            a.src = src;
            a.currentTime = 0;
        }
        a.play().catch(() => { });
    }, [activeId, muted]);

    useEffect(() => {
        if (muted || nextId == null) return;
        const src = trackFor(nextId);
        if (!src) return;
        const pre = new Audio();
        pre.preload = "auto";
        pre.src = src;
    }, [nextId, muted]);

    useEffect(() => {
        const onVis = () => {
            const a = audioRef.current;
            if (!a) return;
            if (document.hidden) a.pause();
            else if (!muted && a.src) a.play().catch(() => { });
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [muted]);

    const toggleVoice = () => {
        const next = !muted;
        setMuted(next);
        try {
            localStorage.setItem(VOICE_KEY, next ? "0" : "1");
        } catch { }
    };

    const onScroll = (e) => {
        const el = e.currentTarget;
        if (!el.clientHeight) return;
        const idx = Math.round(el.scrollTop / el.clientHeight);
        setActiveIndex((cur) => (cur === idx ? cur : idx));
    };

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        window.Tawk_API?.hideWidget?.();
        return () => window.Tawk_API?.showWidget?.();
    }, []);

    const go = useCallback((dir) => {
        const el = scrollRef.current;
        if (el) el.scrollBy({ top: dir * el.clientHeight, behavior: "smooth" });
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                if (shareItem) setShareItem(null);
                else onClose();
            }
            if (e.key === "ArrowDown") go(1);
            if (e.key === "ArrowUp") go(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go, onClose, shareItem]);

    const shareUrl = shareItem ? `${window.location.origin}${detailPath(shareItem)}` : "";
    const shareText = shareItem ? getTitle(shareItem) : "";

    const shareWhatsapp = () => {
        window.open(
            `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
            "_blank",
            "noopener,noreferrer"
        );
        setShareItem(null);
    };

    const shareTelegram = () => {
        window.open(
            `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
            "_blank",
            "noopener,noreferrer"
        );
        setShareItem(null);
    };

    const shareCopy = () => {
        copyToClipboard(shareUrl)
            .then(() => toast.success("Link kopyalandı!"))
            .catch(() => toast.error("Linki kopyalamaq mümkün olmadı."));
        setShareItem(null);
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black">
            <div className="relative mx-auto h-full w-full md:max-w-[600px]">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 left-3 z-20 h-11 w-11 rounded-full bg-black/50 text-white flex items-center justify-center"
                >
                    <ArrowLeft size={22} weight="bold" />
                </button>

                <button
                    type="button"
                    onClick={toggleVoice}
                    className="absolute top-6 right-3 z-20 h-11 w-11 rounded-full bg-black/50 text-white flex items-center justify-center"
                >
                    {muted ? <SpeakerSlash size={22} weight="bold" /> : <SpeakerHigh size={22} weight="bold" />}
                </button>

                {isLoading ? (
                    <div className="h-full flex items-center justify-center text-white">
                        <Spinner size={32} className="animate-spin" />
                    </div>
                ) : noPackage ? (
                    <div className="h-full flex flex-col items-center justify-center gap-4 text-white px-8 text-center">
                        <p className="text-sm text-white/80">
                            Aktiv paketiniz yoxdur və ya bitmişdir. Elanları görmək üçün paket almalısınız.
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                onClose();
                                window.location.href = "/payments";
                            }}
                            className="h-11 px-6 rounded-xl bg-primary text-primary-foreground font-semibold"
                        >
                            Ödəniş səhifəsinə keç
                        </button>
                    </div>
                ) : isError || items.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-white/70 text-sm px-8 text-center">
                        Elan tapılmadı
                    </div>
                ) : (
                    <>
                        <div
                            ref={scrollRef}
                            onScroll={onScroll}
                            className="h-full overflow-y-scroll snap-y snap-mandatory overscroll-contain scrollbar-hidden"
                            style={{ scrollbarWidth: "none" }}
                        >
                            {items.map((p, i) => (
                                <ReelSlide
                                    key={p.idProperty}
                                    p={p}
                                    near={Math.abs(i - activeIndex) <= NEAR_SLIDES}
                                    active={i === activeIndex}
                                    onShare={setShareItem}
                                    onNeedLogin={() => setLoginModal(true)}
                                    onBroken={onBroken}
                                />
                            ))}
                        </div>

                        {isFetchingNextPage && (
                            <div className="pointer-events-none absolute bottom-2 inset-x-0 flex justify-center text-white/70 z-20">
                                <Spinner size={18} className="animate-spin" />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="hidden md:flex flex-col gap-3 absolute right-8 top-1/2 -translate-y-1/2">
                <button
                    type="button"
                    onClick={() => go(-1)}
                    className="h-14 w-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
                >
                    <CaretUp size={22} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={() => go(1)}
                    className="h-14 w-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
                >
                    <CaretDown size={22} weight="bold" />
                </button>
            </div>

            {shareItem && (
                <div
                    className="fixed inset-0 z-30 flex items-end md:items-center justify-center bg-black/60"
                    onClick={() => setShareItem(null)}
                >
                    <div
                        className="w-full md:max-w-sm rounded-t-2xl md:rounded-2xl bg-card text-foreground p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold">Paylaş</h3>
                            <button type="button" onClick={() => setShareItem(null)} className="p-1 text-muted-foreground">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                type="button"
                                onClick={shareWhatsapp}
                                className="flex flex-col items-center gap-2 text-xs font-semibold"
                            >
                                <span className="h-14 w-14 rounded-full bg-[#25D366] text-white flex items-center justify-center">
                                    <WhatsappLogo size={28} weight="fill" />
                                </span>
                                WhatsApp
                            </button>
                            <button
                                type="button"
                                onClick={shareTelegram}
                                className="flex flex-col items-center gap-2 text-xs font-semibold"
                            >
                                <span className="h-14 w-14 rounded-full bg-[#229ED9] text-white flex items-center justify-center">
                                    <TelegramLogo size={28} weight="fill" />
                                </span>
                                Telegram
                            </button>
                            <button
                                type="button"
                                onClick={shareCopy}
                                className="flex flex-col items-center gap-2 text-xs font-semibold"
                            >
                                <span className="h-14 w-14 rounded-full bg-[#3B82F6] text-white flex items-center justify-center">
                                    <Copy size={26} weight="bold" />
                                </span>
                                Kopyala
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loginModal && (
                <div
                    className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setLoginModal(false)}
                >
                    <div
                        className="w-full max-w-sm rounded-2xl bg-card text-foreground p-6 text-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold">Giriş tələb olunur</h3>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Sistemdən daha rahat istifadə üçün şəxsi kabinetidən istifadə tələb edilir.
                        </p>
                        <div className="mt-5 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setLoginModal(false)}
                                className="flex-1 h-11 rounded-xl border border-border font-semibold"
                            >
                                Xeyr
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    nav("/login");
                                }}
                                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold"
                            >
                                Davam et
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}